"use client";

import { useCallback, useEffect } from "react";

/** Raised on <html> for the duration of a printout started from an opted-in page. */
const PRINT_SCOPE_ATTR = "data-print-scope";

/**
 * Prints the current page through the browser dialog, with the document marked
 * as `[data-print-scope]` while it happens.
 *
 * That flag is what the `@media print` rules in `globals.css` key off, so a page
 * that calls this hook drops the shared chrome (anything tagged
 * `data-print-hide`) and keeps its brand colours on paper, while every page that
 * doesn't call it still prints exactly as the browser sees fit.
 * @returns the callback to wire to the "Imprimer" control.
 */
export function usePrintPage() {
  useEffect(() => {
    const root = document.documentElement;
    const mark = () => root.setAttribute(PRINT_SCOPE_ATTR, "");
    const clear = () => root.removeAttribute(PRINT_SCOPE_ATTR);

    window.addEventListener("beforeprint", mark);
    window.addEventListener("afterprint", clear);

    return () => {
      window.removeEventListener("beforeprint", mark);
      window.removeEventListener("afterprint", clear);
      // Unmounting mid-print must not leave the flag behind for the next page.
      clear();
    };
  }, []);

  return useCallback(() => {
    // Set it here too: `beforeprint` covers Ctrl+P, but marking up front means
    // the button path doesn't depend on that event firing at all.
    document.documentElement.setAttribute(PRINT_SCOPE_ATTR, "");
    window.print();
  }, []);
}

export default usePrintPage;
